import { createSlice } from "@reduxjs/toolkit";

import {
  buildLinkedItemTargets,
  forEachTargetState,
  stripImageRelations,
} from "./linkedItemHelpers";

// Puts an updated requirement into every assessment in state that holds it
const replaceAssessmentItem = (state, preqItem) => {
  for (const assessment of preqItem.assessment || []) {
    const current = state.find(item => item.id === assessment.id);
    const index = current?.prequalificationItem?.findIndex(item => item.id === preqItem.id) ?? -1;
    if (index !== -1) {
      current.prequalificationItem[index] = preqItem;
    }
  }
};

const assessmentSlice = createSlice({
  name: "assessment",
  initialState: [],
  reducers: {
    assessmentAdded(state, action) {
      return action.payload;
    },
    assessmentSpecificAdded(state, action) {
      const assessment = action.payload;
      if (state.length === 0) {
        return [action.payload];
      } else {
        const assessmentIndex = state.findIndex(item => item.id === assessment.id);
        if (assessmentIndex > -1) {
          state[assessmentIndex] = assessment;
        } else {
          state.push(assessment);
        }
      }
    },

    assessmentSend(state, action) {
      const assessment = action.payload;
      const assessmentIndex = state.findIndex(item => item.id === assessment.id);
      if (assessmentIndex !== -1) {
        state[assessmentIndex] = assessment;
      }
    },

    assessmentItemImageUpdate(state, action) {
      const image = action.payload;

      const imageTargetsByAssessmentId = buildLinkedItemTargets(
        image.prequalificationItem,
        "assessment",
      );
      const normalizedImage = stripImageRelations(image);

      forEachTargetState(
        state,
        imageTargetsByAssessmentId,
        (_assessmentState, prequalificationItemById, itemIds) => {
          for (const itemId of itemIds) {
            const prequalificationItem = prequalificationItemById.get(itemId);
            if (!prequalificationItem) {
              continue;
            }

            const imageIndex =
              prequalificationItem.image?.findIndex(item => item.id === image.id) ?? -1;
            if (imageIndex === -1) {
              continue;
            }

            prequalificationItem.image[imageIndex] = { ...normalizedImage };
          }
        },
      );
    },

    // A requirement can belong to several assessments: a merged one connects the original items
    // of its sources. Assessments from the list views carry no items — skip them instead of
    // throwing, which would discard the update for the assessment on screen as well.
    assessmentItemUpdate(state, action) {
      replaceAssessmentItem(state, action.payload);
    },

    // Documents (de)activated on a requirement, in whichever assessment holds it
    assessmentItemImageStatusUpdate(state, action) {
      const { prequalificationItemId, active, id: fileId } = action.payload.file;

      for (const assessment of state) {
        const preqItem = assessment.prequalificationItem?.find(
          item => item.id === prequalificationItemId,
        );
        const image = preqItem?.image?.find(img => img.id === fileId);
        if (image) {
          image.active = active;
        }
      }
    },
    assessmentMoveImage(state, action) {
      const image = action.payload.payload;
      const destinationId = action.payload.destinationId;
      const preqItemId = action.payload.preqItemId;

      const imageTargetsByAssessmentId = buildLinkedItemTargets(
        image.prequalificationItem,
        "assessment",
      );
      const normalizedImage = stripImageRelations(image);

      forEachTargetState(
        state,
        imageTargetsByAssessmentId,
        (_assessmentState, prequalificationItemById, sourceItemIds) => {
          if (!sourceItemIds.has(preqItemId)) {
            return;
          }

          const fromItem = prequalificationItemById.get(preqItemId);
          const toItem = prequalificationItemById.get(destinationId);
          if (!fromItem || !toItem) {
            return;
          }

          const imageIndex = fromItem.image?.findIndex(item => item.id === image.id) ?? -1;
          if (imageIndex === -1) {
            return;
          }

          fromItem.image.splice(imageIndex, 1);

          const destinationImageIndex = toItem.image?.findIndex(item => item.id === image.id) ?? -1;
          if (destinationImageIndex === -1) {
            if (Array.isArray(toItem.image)) {
              toItem.image.push({ ...normalizedImage });
            } else {
              toItem.image = [{ ...normalizedImage }];
            }
          } else {
            toItem.image[destinationImageIndex] = { ...normalizedImage };
          }
        },
      );
    },
    assessmentFileAdd(state, action) {
      const id = action.payload.preqItemId;
      const file = action.payload.response;
      const assessmentId = action.payload.assessmentId;

      const preqIndex = state.findIndex(item => item.id === assessmentId);
      if (preqIndex === -1) {
        return;
      }

      const toChange =
        state[preqIndex].prequalificationItem?.findIndex(item => item.id === id) ?? -1;
      if (toChange === -1) {
        return;
      }

      const targetItem = state[preqIndex].prequalificationItem[toChange];
      if (Array.isArray(targetItem.image)) {
        targetItem.image.push(file);
      } else {
        targetItem.image = [file];
      }
    },
    assessmentFileDelete(state, action) {
      const file = action.payload.file;
      const targets = buildLinkedItemTargets(file.prequalificationItem, "assessment");

      if (targets.size > 0) {
        forEachTargetState(
          state,
          targets,
          (_assessmentState, prequalificationItemById, itemIds) => {
            for (const itemId of itemIds) {
              const prequalificationItem = prequalificationItemById.get(itemId);
              if (!prequalificationItem?.image) {
                continue;
              }

              prequalificationItem.image = prequalificationItem.image.filter(
                image => image.id !== file.id,
              );
            }
          },
        );
        return;
      }

      // A file without links is removed from the assessment it was deleted in. Its own
      // `prequalificationId` names a prequalification, never an assessment.
      const assessment = state.find(item => item.id === action.payload.assessmentId);
      for (const prequalificationItem of assessment?.prequalificationItem || []) {
        if (prequalificationItem.image?.some(image => image.id === file.id)) {
          prequalificationItem.image = prequalificationItem.image.filter(
            image => image.id !== file.id,
          );
        }
      }
    },
    assessmentItemImageNameUpdate(state, action) {
      const imageObject = action.payload;

      const imageTargetsByAssessmentId = buildLinkedItemTargets(
        imageObject.prequalificationItem,
        "assessment",
      );

      forEachTargetState(
        state,
        imageTargetsByAssessmentId,
        (_assessmentState, prequalificationItemById, itemIds) => {
          for (const itemId of itemIds) {
            const prequalificationItem = prequalificationItemById.get(itemId);
            if (!prequalificationItem) {
              continue;
            }

            const image = prequalificationItem.image?.find(item => item.id === imageObject.id);
            if (!image) {
              continue;
            }

            image.imageBaseName = imageObject.imageBaseName;
          }
        },
      );
    },
    /**
     * Updates a specific assessment in the allAssessments array
     */
    updateAssessmentInAll: (state, action) => {
      const assessment = action.payload;
      const index = state.findIndex(item => item.id === assessment.id);
      if (index !== -1) {
        state[index] = assessment;
      } else {
        state.unshift(assessment); // Add to beginning if not found
      }
    },
  },
});

export const {
  assessmentAdded,
  assessmentSend,
  assessmentSpecificAdded,
  assessmentMoveImage,
  assessmentItemImageStatusUpdate,
  assessmentItemImageUpdate,
  assessmentItemUpdate,
  assessmentFileDelete,
  assessmentFileAdd,
  assessmentItemImageNameUpdate,
  updateAssessmentInAll,
} = assessmentSlice.actions;
export default assessmentSlice.reducer;
